import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Sun, CheckCircle2, Circle, Clock, AlertTriangle,
  FileText, Link as LinkIcon, MessageSquare, ChevronRight, Zap
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PHASE_COLORS = [
  "#3B82F6","#8B5CF6","#22D3EE","#10B981","#F59E0B","#EF4444","#EC4899","#6366F1"
];

function CircleProgress({ pct, size = 96, stroke = 6, color = "#3B82F6" }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(46,100,68,0.1)" strokeWidth={stroke} />
      <circle
        cx={size/2} cy={size/2} r={r} fill="none"
        stroke={color} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={circ} strokeDashoffset={offset}
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ filter: `drop-shadow(0 0 6px ${color}80)`, transition: "stroke-dashoffset 1s ease" }}
      />
      <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle"
        fill="white" fontSize={size * 0.22} fontWeight="800" fontFamily="Outfit">
        {pct}%
      </text>
    </svg>
  );
}

const RAGBadge = ({ status }) => {
  const map = {
    green: { bg: "rgba(16,185,129,0.12)", color: "#34D399", label: "On Track", dot: "#34D399" },
    amber: { bg: "rgba(245,158,11,0.12)",  color: "#FBBF24", label: "At Risk",  dot: "#FBBF24" },
    red:   { bg: "rgba(239,68,68,0.12)",   color: "#F87171", label: "Delayed",  dot: "#F87171" },
  };
  const s = map[status] || map.green;
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: "11px", fontWeight: "700", letterSpacing: "0.06em", padding: "4px 10px", borderRadius: "100px", display: "inline-flex", alignItems: "center", gap: "6px" }}>
      <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: s.dot, boxShadow: `0 0 8px ${s.dot}` }} />
      {s.label}
    </span>
  );
};

export default function ClientPortalPage() {
  const { shareToken } = useParams();
  const [data, setData]   = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data: res } = await axios.get(`${API}/client/portal/${shareToken}`);
        setData(res);
      } catch (err) {
        setError(err.response?.data?.detail || "Project not found or link has expired.");
      }
    };
    fetch();
  }, [shareToken]);

  if (error) {
    return (
      <div style={{ background: "#0B0B0F", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Manrope, sans-serif" }}>
        <div style={{ textAlign: "center", padding: "32px" }}>
          <div style={{ width: "48px", height: "48px", borderRadius: "14px", background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.25)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <AlertTriangle size={22} style={{ color: "#F87171" }} />
          </div>
          <h2 style={{ fontFamily: "Outfit", fontWeight: "700", fontSize: "20px", color: "white", marginBottom: "8px" }}>Link Not Found</h2>
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "14px" }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ background: "#0B0B0F", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: "40px", height: "40px", borderRadius: "12px", background: "linear-gradient(135deg, #3B82F6, #6366F1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", boxShadow: "0 0 24px rgba(59,130,246,0.4)" }}>
            <Sun size={18} color="white" />
          </div>
          <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "13px", fontFamily: "Manrope, sans-serif" }}>Loading project...</div>
        </div>
      </div>
    );
  }

  const { project, workspace, phase_stats, milestones, recent_dprs, shared_docs } = data;
  const phases = phase_stats || [];
  const dprs = recent_dprs || [];
  const shared_documents = shared_docs || [];
  const pct = project.completion_percentage || 0;
  const shareUrl = window.location.href;

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const completedMilestones = milestones?.filter(m => m.status === "completed") || [];
  const pendingMilestones   = milestones?.filter(m => m.status !== "completed") || [];

  return (
    <div style={{ background: "#0B0B0F", minHeight: "100vh", fontFamily: "Manrope, sans-serif", color: "#E8ECF0" }}>

      {/* Sticky Header */}
      <header style={{
        position: "sticky", top: 0, zIndex: 40,
        background: "rgba(11,11,15,0.9)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}>
        <div style={{ maxWidth: "960px", margin: "0 auto", padding: "0 20px", height: "60px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {workspace?.logo_url ? (
              <img src={`${BACKEND}${workspace.logo_url}`} alt="logo" style={{ height: "28px", width: "auto", maxWidth: "80px", objectFit: "contain", borderRadius: "4px" }} />
            ) : (
              <div style={{ width: "28px", height: "28px", borderRadius: "8px", background: "linear-gradient(135deg, #3B82F6, #6366F1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Sun size={13} color="white" />
              </div>
            )}
            <span style={{ fontFamily: "Outfit", fontWeight: "700", fontSize: "15px", color: "white" }}>{workspace?.name || "SunBiz"}</span>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleCopy}
              style={{
                display: "flex", alignItems: "center", gap: "6px", padding: "7px 12px",
                background: copied ? "rgba(16,185,129,0.12)" : "rgba(46,100,68,0.08)",
                border: `1px solid ${copied ? "rgba(16,185,129,0.3)" : "rgba(255,255,255,0.08)"}`,
                borderRadius: "8px", fontSize: "12px", fontWeight: "600", color: copied ? "#34D399" : "rgba(255,255,255,0.5)",
                cursor: "pointer", transition: "all 0.2s ease",
              }}
            >
              <LinkIcon size={12} />
              {copied ? "Copied!" : "Copy Link"}
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`Project Update: ${project.name} — ${pct}% complete. View live: ${shareUrl}`)}`}
              target="_blank" rel="noopener noreferrer"
              style={{
                display: "flex", alignItems: "center", gap: "6px", padding: "7px 12px",
                background: "rgba(37,211,102,0.08)", border: "1px solid rgba(37,211,102,0.2)",
                borderRadius: "8px", fontSize: "12px", fontWeight: "600", color: "#4ADE80",
                textDecoration: "none", transition: "all 0.2s ease",
              }}
            >
              <MessageSquare size={12} />
              WhatsApp
            </a>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: "960px", margin: "0 auto", padding: "28px 20px 60px" }}>

        {/* Hero Card */}
        <div
          style={{
            background: "rgba(59,130,246,0.06)",
            border: "1px solid rgba(59,130,246,0.18)",
            borderRadius: "20px",
            padding: "28px",
            marginBottom: "24px",
            boxShadow: "0 0 40px rgba(59,130,246,0.08) inset",
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "24px" }}>
            <CircleProgress pct={pct} />
            <div style={{ flex: 1, minWidth: "200px" }}>
              <div style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginBottom: "6px" }}>Live Project Update</div>
              <h1 style={{ fontFamily: "Outfit", fontWeight: "800", fontSize: "clamp(18px, 3vw, 26px)", color: "white", letterSpacing: "-0.03em", marginBottom: "8px" }}>{project.name}</h1>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "12px" }}>
                <RAGBadge status={project.rag_status} />
                <span style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "100px", padding: "4px 10px", fontSize: "11px", color: "rgba(255,255,255,0.5)" }}>
                  {project.capacity_kwp} kWp
                </span>
                {project.location && (
                  <span style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "100px", padding: "4px 10px", fontSize: "11px", color: "rgba(255,255,255,0.5)" }}>
                    {project.location}
                  </span>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px" }}>
                {[
                  { label: "Start Date",      value: project.start_date ? new Date(project.start_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—" },
                  { label: "Target Completion", value: project.expected_end_date ? new Date(project.expected_end_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—" },
                  { label: "Project Type",    value: project.project_type?.replace(/_/g, " ") || "—" },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div style={{ fontSize: "10px", fontWeight: "600", letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.25)", marginBottom: "3px" }}>{label}</div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "rgba(255,255,255,0.75)", textTransform: "capitalize" }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Phase Journey */}
        {phases?.length > 0 && (
          <div style={{ background: "#16161D", border: "1px solid rgba(46,100,68,0.1)", borderRadius: "16px", padding: "22px", marginBottom: "20px" }}>
            <h2 style={{ fontFamily: "Outfit", fontWeight: "700", fontSize: "14px", color: "white", letterSpacing: "-0.02em", marginBottom: "18px" }}>Phase Progress</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              {phases.map((phase, i) => {
                const phasePct = phase.total > 0 ? Math.round((phase.completed / phase.total) * 100) : (phase.pct || 0);
                const color = PHASE_COLORS[i % PHASE_COLORS.length];
                return (
                  <div key={phase.phase || i}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: color, boxShadow: `0 0 8px ${color}` }} />
                        <span style={{ fontSize: "13px", fontWeight: "600", color: "rgba(255,255,255,0.75)" }}>{phase.phase_name || phase.name}</span>
                      </div>
                      <span style={{ fontSize: "11px", fontWeight: "700", color: "rgba(255,255,255,0.4)" }}>{phasePct}%</span>
                    </div>
                    <div style={{ height: "4px", background: "rgba(255,255,255,0.06)", borderRadius: "4px", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${phasePct}%`, background: color, borderRadius: "4px", boxShadow: `0 0 8px ${color}60`, transition: "width 0.8s ease" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px", marginBottom: "20px" }}>
          {/* Completed Milestones */}
          {completedMilestones.length > 0 && (
            <div style={{ background: "#16161D", border: "1px solid rgba(46,100,68,0.1)", borderRadius: "16px", padding: "20px" }}>
              <h2 style={{ fontFamily: "Outfit", fontWeight: "700", fontSize: "14px", color: "white", letterSpacing: "-0.02em", marginBottom: "14px", display: "flex", alignItems: "center", gap: "8px" }}>
                <CheckCircle2 size={14} style={{ color: "#34D399" }} /> Completed
              </h2>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {completedMilestones.slice(0, 6).map((m, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                    <div style={{ width: "18px", height: "18px", borderRadius: "50%", background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "1px" }}>
                      <CheckCircle2 size={10} style={{ color: "#34D399" }} />
                    </div>
                    <div>
                      <div style={{ fontSize: "13px", fontWeight: "500", color: "rgba(255,255,255,0.7)" }}>{m.name}</div>
                      {(m.actual_completion || m.completed_date) && <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.3)", marginTop: "1px" }}>{new Date(m.actual_completion || m.completed_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming Milestones */}
          {pendingMilestones.length > 0 && (
            <div style={{ background: "#16161D", border: "1px solid rgba(46,100,68,0.1)", borderRadius: "16px", padding: "20px" }}>
              <h2 style={{ fontFamily: "Outfit", fontWeight: "700", fontSize: "14px", color: "white", letterSpacing: "-0.02em", marginBottom: "14px", display: "flex", alignItems: "center", gap: "8px" }}>
                <Clock size={14} style={{ color: "#FBBF24" }} /> Upcoming
              </h2>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {pendingMilestones.slice(0, 6).map((m, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                    <div style={{ width: "18px", height: "18px", borderRadius: "50%", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "1px" }}>
                      <Circle size={8} style={{ color: "#FBBF24" }} />
                    </div>
                    <div>
                      <div style={{ fontSize: "13px", fontWeight: "500", color: "rgba(255,255,255,0.6)" }}>{m.name}</div>
                      {(m.planned_end || m.target_date) && <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.25)", marginTop: "1px" }}>Target: {new Date(m.planned_end || m.target_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Recent Site Activity (DPRs) */}
        {dprs?.length > 0 && (
          <div style={{ background: "#16161D", border: "1px solid rgba(46,100,68,0.1)", borderRadius: "16px", padding: "20px", marginBottom: "20px" }}>
            <h2 style={{ fontFamily: "Outfit", fontWeight: "700", fontSize: "14px", color: "white", letterSpacing: "-0.02em", marginBottom: "14px" }}>Recent Site Activity</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
              {dprs.slice(0, 5).map((dpr, i) => (
                <div
                  key={dpr.id || i}
                  style={{
                    display: "flex", alignItems: "center", gap: "14px",
                    padding: "12px 0",
                    borderBottom: i < Math.min(dprs.length - 1, 4) ? "1px solid rgba(46,100,68,0.08)" : "none",
                  }}
                >
                  <div style={{ width: "32px", height: "32px", borderRadius: "10px", background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Zap size={14} style={{ color: "#60A5FA" }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "rgba(255,255,255,0.75)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {dpr.work_done || dpr.summary || "Daily Progress Report"}
                    </div>
                    <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.3)", marginTop: "2px" }}>
                      {dpr.date} · {dpr.manpower_deployed ? `${dpr.manpower_deployed} workers` : ""}
                    </div>
                  </div>
                  <div style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.06em", textTransform: "uppercase", padding: "3px 8px", borderRadius: "100px", background: "rgba(59,130,246,0.1)", color: "#60A5FA", flexShrink: 0 }}>
                    DPR
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Shared Documents */}
        {shared_documents?.length > 0 && (
          <div style={{ background: "#16161D", border: "1px solid rgba(46,100,68,0.1)", borderRadius: "16px", padding: "20px", marginBottom: "20px" }}>
            <h2 style={{ fontFamily: "Outfit", fontWeight: "700", fontSize: "14px", color: "white", letterSpacing: "-0.02em", marginBottom: "14px" }}>
              Shared Documents
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {shared_documents.map((doc, i) => (
                <div key={doc.id || i} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 12px", background: "rgba(255,255,255,0.03)", borderRadius: "10px", border: "1px solid rgba(46,100,68,0.08)" }}>
                  <div style={{ width: "30px", height: "30px", borderRadius: "8px", background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <FileText size={13} style={{ color: "#A78BFA" }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "13px", fontWeight: "500", color: "rgba(255,255,255,0.7)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.file_name || doc.name}</div>
                    <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.25)", marginTop: "1px" }}>{doc.file_type === "financial/pi" ? "Proforma Invoice (PDF)" : (doc.file_type || doc.document_type || doc.folder)}</div>
                  </div>
                  {doc.file_url && (
                    <a href={`${BACKEND}${doc.file_url}`} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: "12px", fontWeight: "600", color: "#60A5FA", textDecoration: "none", padding: "5px 10px", background: "rgba(59,130,246,0.1)", borderRadius: "6px" }}>
                      View
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: "center", paddingTop: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginBottom: "6px" }}>
            <div style={{ width: "22px", height: "22px", borderRadius: "6px", background: "linear-gradient(135deg, #3B82F6, #6366F1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Sun size={11} color="white" />
            </div>
            <span style={{ fontFamily: "Outfit", fontWeight: "700", fontSize: "13px", color: "rgba(255,255,255,0.5)" }}>SunBiz</span>
          </div>
          <p style={{ fontSize: "11px", color: "rgba(255,255,255,0.2)" }}>
            Powered by SunBiz by Sun Bizcon · This is a read-only client view
          </p>
        </div>

      </div>
    </div>
  );
}
